import { createMemo, createSignal, For, onCleanup, Show } from "solid-js";
import {
  fromSquare,
  inDeployZone,
  isInCheck,
  isPrinceThreatened,
  outpostNodes,
  pieceDef,
  squareName,
  toSquare,
  type PieceTypeId,
  type PlayerId,
  type Square,
} from "../../core";
import type { GameController } from "../../app/controller";
import { PieceIcon } from "./PieceIcon";
import { TerrainTile } from "./TerrainTile";

/** Transient state for a piece being dragged across the board. */
interface DragState {
  readonly from: Square;
  readonly type: PieceTypeId;
  readonly owner: PlayerId;
  readonly x: number;
  readonly y: number;
}

export function Board(props: { controller: GameController }) {
  const c = props.controller;
  const state = () => c.store.state;
  const cfg = () => state().config;

  // Drag-to-move: lifting a piece selects it (so legal targets light up) and
  // floats a glyph under the cursor; dropping on a legal square plays the move.
  // Tap/click still works — the cell's onClick handles selection and targets.
  const [drag, setDrag] = createSignal<DragState | null>(null);

  function endDrag() {
    setDrag(null);
    window.removeEventListener("pointermove", onPointerMove);
    window.removeEventListener("pointerup", onPointerUp);
    window.removeEventListener("pointercancel", onPointerCancel);
  }
  onCleanup(endDrag);

  function onPointerMove(e: PointerEvent) {
    setDrag((d) => (d ? { ...d, x: e.clientX, y: e.clientY } : d));
  }

  function onPointerCancel() {
    endDrag();
  }

  function onPointerUp(e: PointerEvent) {
    const d = drag();
    endDrag();
    if (!d) return;
    // Resolve the square under the pointer. The ghost is pointer-events:none,
    // so elementFromPoint reports the cell beneath it.
    const el = document.elementFromPoint(e.clientX, e.clientY);
    const cell = el?.closest<HTMLElement>(".cell");
    if (!cell?.dataset.sq) return;
    const to = Number(cell.dataset.sq);
    if (Number.isNaN(to) || to === d.from) return;
    // The piece is already selected (from pointerdown), so selectSquare plays
    // the move when `to` is a legal target and is otherwise a no-op.
    c.selectSquare(to);
  }

  function onPiecePointerDown(e: PointerEvent, sq: Square) {
    if (e.button !== 0) return; // primary button / touch only
    const piece = state().board[sq];
    if (!piece || !c.canAct() || piece.owner !== state().turn) return;
    e.preventDefault();
    c.selectSquare(sq); // select so legal targets are highlighted while dragging
    setDrag({ from: sq, type: piece.type, owner: piece.owner, x: e.clientX, y: e.clientY });
    window.addEventListener("pointermove", onPointerMove);
    window.addEventListener("pointerup", onPointerUp);
    window.addEventListener("pointercancel", onPointerCancel);
  }

  // Orient the board to the viewer: their own pieces sit at the bottom, like a
  // normal chess board. Seat 0 sees rank 0 at the bottom; seat 1 sees a 180°
  // flip (their home rank at the bottom, files right-to-left).
  const flip = () => c.viewer() === 1;

  // Squares in display order (top row first), respecting the flip.
  const cells = createMemo<Square[]>(() => {
    const { width, height } = cfg();
    const out: Square[] = [];
    if (flip()) {
      for (let rank = 0; rank < height; rank++) {
        for (let file = width - 1; file >= 0; file--) out.push(rank * width + file);
      }
    } else {
      for (let rank = height - 1; rank >= 0; rank--) {
        for (let file = 0; file < width; file++) out.push(rank * width + file);
      }
    }
    return out;
  });

  // Highlight the squares touched by the most recent action.
  const lastMove = createMemo<Set<Square>>(() => {
    const log = state().log;
    const entry = log[log.length - 1];
    const set = new Set<Square>();
    if (!entry) return set;
    if (entry.kind === "move") {
      set.add(entry.from);
      set.add(entry.to);
    } else if (entry.kind === "deploy") {
      set.add(entry.to);
    }
    return set;
  });

  // The square where a piece was just deployed — used for a one-shot scale-in.
  const justDeployed = createMemo<Square | null>(() => {
    const log = state().log;
    const entry = log[log.length - 1];
    return entry?.kind === "deploy" ? entry.to : null;
  });

  // The square where a capture just happened — used for a brief flash. Keyed by
  // ply via the classList re-render so a fresh capture restarts the animation.
  const justCaptured = createMemo<Square | null>(() => {
    const log = state().log;
    const entry = log[log.length - 1];
    if ((entry?.kind === "move" || entry?.kind === "captainMove") && entry.captured) {
      return entry.to;
    }
    return null;
  });

  // The viewer's current Outpost deployment nodes (where they may deploy beyond
  // their home ranks). Re-rolls each turn, so it's a memo on the live state.
  const nodeSquares = createMemo<Set<Square>>(
    () => new Set(outpostNodes(state(), c.viewer())),
  );

  // Which player's king is currently in check?
  const kingsInCheck = createMemo<Set<PlayerId>>(() => {
    const s = state();
    const set = new Set<PlayerId>();
    for (const owner of [0, 1] as const) {
      if (isInCheck(s, owner)) set.add(owner);
    }
    return set;
  });

  // Which player's Prince is under attack? (Losing it exposes the King.)
  const princesThreatened = createMemo<Set<PlayerId>>(() => {
    const s = state();
    const set = new Set<PlayerId>();
    for (const owner of [0, 1] as const) {
      if (isPrinceThreatened(s, owner)) set.add(owner);
    }
    return set;
  });

  // --- keyboard navigation ---------------------------------------------------
  // A focus cursor lets the board be played without a mouse: arrow keys move it,
  // Enter/Space selects (the same path a click takes), Escape clears. A roving
  // tabindex keeps a single board tab-stop; cell refs let us move focus.
  const initialCursor = () => {
    const { width, height } = cfg();
    return toSquare(Math.floor(width / 2), c.viewer() === 0 ? 0 : height - 1, width);
  };
  const [cursor, setCursor] = createSignal<Square>(initialCursor());
  const cellRefs = new Map<Square, HTMLElement>();

  function moveCursor(sq: Square) {
    setCursor(sq);
    cellRefs.get(sq)?.focus();
  }

  function onBoardKeyDown(e: KeyboardEvent) {
    const { width, height } = cfg();
    let { file, rank } = fromSquare(cursor(), width);
    // Arrow keys move by VISUAL direction; when the board is flipped, visual
    // up/right map to lower rank / lower file.
    const up = flip() ? -1 : 1;
    const right = flip() ? -1 : 1;
    switch (e.key) {
      case "ArrowUp": rank = Math.max(0, Math.min(height - 1, rank + up)); break;
      case "ArrowDown": rank = Math.max(0, Math.min(height - 1, rank - up)); break;
      case "ArrowLeft": file = Math.max(0, Math.min(width - 1, file - right)); break;
      case "ArrowRight": file = Math.max(0, Math.min(width - 1, file + right)); break;
      case "Enter":
      case " ":
        e.preventDefault();
        c.selectSquare(cursor());
        return;
      case "Escape":
        c.clearSelection();
        return;
      default:
        return;
    }
    e.preventDefault();
    moveCursor(toSquare(file, rank, width));
  }

  /** A spoken description of a square: its name, occupant, and status. */
  const cellLabel = (sq: Square): string => {
    const name = squareName(sq, cfg());
    const p = state().board[sq];
    if (!p) {
      const t = c.targets().get(sq);
      return t ? `${name}, empty, legal move` : `${name}, empty`;
    }
    const who = p.owner === c.viewer() ? "your" : "enemy";
    const inCheck = p.type === "king" && kingsInCheck().has(p.owner) ? ", in check" : "";
    const threatened =
      p.type === "prince" && princesThreatened().has(p.owner) ? ", under attack" : "";
    const takeable = c.targets().get(sq)?.capture ? ", can capture" : "";
    return `${name}, ${who} ${pieceDef(p.type).name}${inCheck}${threatened}${takeable}`;
  };

  return (
    <div class="board-wrap">
      <div
        class="board"
        style={{ "--cells": cfg().width }}
        role="grid"
        aria-label="Game board"
        aria-rowcount={cfg().height}
        aria-colcount={cfg().width}
        onKeyDown={onBoardKeyDown}
      >
        <For each={cells()}>
          {(sq) => {
            const fr = () => fromSquare(sq, cfg().width);
            const isLight = () => (fr().file + fr().rank) % 2 === 1;
            const piece = () => state().board[sq];
            const target = () => c.targets().get(sq);
            const isSelected = () =>
              c.selection()?.kind === "piece" &&
              (c.selection() as { square: Square }).square === sq;
            const isCardSel = () => c.selection()?.kind === "card";
            // Coordinate labels sit on the visually bottom row + leftmost column,
            // which depend on the board's orientation.
            const showFile = () => fr().rank === (flip() ? cfg().height - 1 : 0);
            const showRank = () => fr().file === (flip() ? cfg().width - 1 : 0);
            const checkedKing = () => {
              const p = piece();
              return !!p && p.type === "king" && kingsInCheck().has(p.owner);
            };
            const threatenedPrince = () => {
              const p = piece();
              return !!p && p.type === "prince" && princesThreatened().has(p.owner);
            };

            return (
              <div
                class="cell"
                data-sq={sq}
                ref={(el) => cellRefs.set(sq, el)}
                role="gridcell"
                aria-label={cellLabel(sq)}
                aria-rowindex={flip() ? fr().rank + 1 : cfg().height - fr().rank}
                aria-colindex={flip() ? cfg().width - fr().file : fr().file + 1}
                aria-selected={isSelected()}
                tabindex={sq === cursor() ? 0 : -1}
                classList={{
                  light: isLight(),
                  dark: !isLight(),
                  selected: isSelected(),
                  lastmove: lastMove().has(sq) && !isSelected(),
                  deploytarget: !!target() && isCardSel(),
                  "outpost-node": nodeSquares().has(sq) && !piece(),
                  check: checkedKing() && state().phase === "playing",
                  checkmate: checkedKing() && state().phase === "finished",
                  "prince-threat": threatenedPrince() && state().phase === "playing",
                  "just-deployed": justDeployed() === sq,
                  "just-captured": justCaptured() === sq,
                }}
                onClick={() => {
                  setCursor(sq);
                  c.selectSquare(sq);
                }}
              >
                <Show when={showRank()}>
                  <span class="coord rank">{fr().rank + 1}</span>
                </Show>
                <Show when={showFile()}>
                  <span class="coord file">{String.fromCharCode(97 + fr().file)}</span>
                </Show>

                <Show when={piece()}>
                  {(p) => {
                    // Two-cell terrain (Valley/Ridge) is one continuous glyph
                    // spanning both squares: each cell paints its own half of
                    // the wide/tall art (full-bleed, so the halves meet with no
                    // seam). The half depends on board geometry AND the flip.
                    const fp = () => pieceDef(p().type).footprint;
                    const terrainHalf = (): "left" | "right" | "top" | "bottom" | null => {
                      const f = fp();
                      if (!f || p().link == null) return null;
                      const width = cfg().width;
                      const me = fromSquare(sq, width);
                      const partner = fromSquare(p().link as Square, width);
                      if (f === "h") {
                        const left = (me.file < partner.file) !== flip();
                        return left ? "left" : "right";
                      }
                      const top = (me.rank > partner.rank) !== flip();
                      return top ? "top" : "bottom";
                    };
                    return (
                      <Show
                        when={terrainHalf()}
                        fallback={
                          <PieceIcon
                            type={p().type}
                            owner={p().owner}
                            exposed={p().type === "king" && kingsInCheck().has(p().owner)}
                            draggable={c.canAct() && p().owner === state().turn}
                            dragging={drag()?.from === sq}
                            onPointerDown={(e) => onPiecePointerDown(e, sq)}
                          />
                        }
                      >
                        {(half) => <TerrainTile type={p().type} half={half()} />}
                      </Show>
                    );
                  }}
                </Show>

                <Show when={target() && !piece()}>
                  <span class="dot" />
                </Show>
                <Show when={target()?.capture && piece()}>
                  <span class="ring" />
                </Show>
              </div>
            );
          }}
        </For>
      </div>

      <Show when={drag()}>
        {(d) => (
          <PieceIcon
            type={d().type}
            owner={d().owner}
            class="drag-ghost"
            style={{ left: `${d().x}px`, top: `${d().y}px` }}
          />
        )}
      </Show>
    </div>
  );
}

// Exposed for potential reuse / testing of deploy-zone shading.
export function isDeploySquare(sq: Square, owner: PlayerId, controller: GameController): boolean {
  return inDeployZone(sq, owner, controller.store.state.config);
}
